import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/dev/api-explorer")({
	component: ApiExplorer,
});

const PRESETS = [
	{
		label: "Rick Astley - Never Gonna Give You Up",
		url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
	},
	{
		label: "Lex Fridman #100",
		url: "https://www.youtube.com/watch?v=Kedt2or9xlo",
	},
	{
		label: "Short youtu.be link",
		url: "https://youtu.be/dQw4w9WgXcQ",
	},
];

interface ApiResult {
	status: "idle" | "loading" | "success" | "error";
	data?: unknown;
	timing_ms?: number;
	error?: string;
}

function ApiExplorer() {
	const [url, setUrl] = useState("");
	const [supadata, setSupadata] = useState<ApiResult>({ status: "idle" });
	const [scrapingfish, setScrapingfish] = useState<ApiResult>({
		status: "idle",
	});

	async function fetchEndpoint(
		endpoint: string,
		targetUrl: string,
		setter: (r: ApiResult) => void,
	) {
		setter({ status: "loading" });
		try {
			const res = await fetch(
				`/dev-api/${endpoint}?url=${encodeURIComponent(targetUrl)}`,
			);
			const json = await res.json();
			if (!res.ok || json.error) {
				setter({
					status: "error",
					error: json.error || `HTTP ${res.status}`,
					timing_ms: json.timing_ms,
				});
			} else {
				setter({
					status: "success",
					data: json.data,
					timing_ms: json.timing_ms,
				});
			}
		} catch (err) {
			setter({
				status: "error",
				error: err instanceof Error ? err.message : String(err),
			});
		}
	}

	function handleSubmit(e: React.FormEvent) {
		e.preventDefault();
		if (!url.trim()) return;
		fetchEndpoint("supadata-video", url, setSupadata);
		fetchEndpoint("scrapingfish", url, setScrapingfish);
	}

	function handlePreset(presetUrl: string) {
		setUrl(presetUrl);
		fetchEndpoint("supadata-video", presetUrl, setSupadata);
		fetchEndpoint("scrapingfish", presetUrl, setScrapingfish);
	}

	return (
		<div className="mx-auto max-w-7xl px-6 py-8">
			<h1 className="mb-1 text-2xl font-semibold">YouTube Scrape</h1>
			<p className="text-muted mb-6 text-sm">
				Compare Supadata and ScrapingFish responses for YouTube URLs.
			</p>

			<form onSubmit={handleSubmit} className="mb-4 flex gap-2">
				<input
					type="url"
					value={url}
					onChange={(e) => setUrl(e.target.value)}
					placeholder="https://www.youtube.com/watch?v=..."
					className="flex-1 rounded border border-white/10 bg-white/5 px-3 py-2 text-sm placeholder:text-white/30 focus:border-yellow-500/50 focus:ring-2 focus:ring-yellow-500/30 focus:outline-none"
				/>
				<button
					type="submit"
					disabled={!url.trim()}
					className="rounded bg-yellow-500 px-4 py-2 text-sm font-medium text-black hover:bg-yellow-400 disabled:opacity-40"
				>
					Fetch
				</button>
			</form>

			<div className="mb-8 flex flex-wrap gap-2">
				{PRESETS.map((p) => (
					<button
						key={p.url}
						onClick={() => handlePreset(p.url)}
						className="rounded border border-white/10 px-2.5 py-1 text-xs hover:border-white/20 hover:bg-white/5"
					>
						{p.label}
					</button>
				))}
			</div>

			<div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
				<ResultPanel
					title="Supadata /youtube/video"
					result={supadata}
				/>
				<ResultPanel
					title="ScrapingFish (render_js)"
					result={scrapingfish}
				/>
			</div>
		</div>
	);
}

function ResultPanel({ title, result }: { title: string; result: ApiResult }) {
	return (
		<div className="rounded border border-white/10">
			<div className="flex items-center justify-between border-b border-white/10 px-4 py-2">
				<h2 className="text-sm font-medium">{title}</h2>
				{result.timing_ms != null && (
					<span className="text-muted text-xs">
						{result.timing_ms}ms
					</span>
				)}
			</div>
			<div className="max-h-[600px] overflow-auto p-4">
				{result.status === "idle" && (
					<p className="text-muted text-sm">
						Enter a URL and click Fetch.
					</p>
				)}
				{result.status === "loading" && (
					<p className="text-muted animate-pulse text-sm">
						Loading...
					</p>
				)}
				{result.status === "error" && (
					<p className="text-sm text-red-400">{result.error}</p>
				)}
				{result.status === "success" && <JsonView data={result.data} />}
			</div>
		</div>
	);
}

function JsonView({ data }: { data: unknown }) {
	const json = JSON.stringify(data, null, 2);

	// Highlight specific fields of interest
	const highlighted = json.replace(
		/("(?:uploadDate|datePublished|publishDate|publishedAt|dates)":\s*(?:"[^"]*"|null|\{[^}]*\}))/g,
		'<mark class="bg-yellow-500/20 text-yellow-300">$1</mark>',
	);

	return (
		<pre
			className="text-xs leading-relaxed break-all whitespace-pre-wrap"
			dangerouslySetInnerHTML={{ __html: highlighted }}
		/>
	);
}
